import type { Axis } from '../constants';
import type { GameState } from '../state';
import type { EvaluatedConstraint, Puzzle } from '../types';

// ──────────────────────────────────────────────────────────────────────
// Multi-stage tutorial data (mirrors iOS TutorialPuzzle.swift)
// ──────────────────────────────────────────────────────────────────────
//
// The tutorial is a sequence of stages. Each stage owns a fresh puzzle
// (cells/bag/constraints) and a sequence of steps. Tutorial.tsx swaps
// the local game state when stages change. Each step optionally:
//
//   - highlights a chip (`chipHighlight`) — the existing pin treatment
//   - highlights a cell (`cellHighlight`) — the existing accent ring
//   - draws an animated arrow from a bag tile to a target cell (`arrow`)
//
// Steps either auto-advance from a game-state predicate (`advance.kind
// === 'predicate'`) or wait for the player to press Next.
//
// iOS is canonical. Stages: Welcome (1) → Glossary (2) → Restrictions (3)
// → Modifiers (4) → Gaps (5).

export type ChipRef = { axis: Axis; index: number };
export type ArrowRef = { tileId: string; toCell: number };

export interface TutorialStep {
  id: string;
  message: string;
  /// Highlight the matching constraint chip via the same pin treatment
  /// the main game uses. When set, the tutorial pushes `pinnedLine`
  /// onto HexBoard.
  chipHighlight?: ChipRef;
  /// Pulse the accent ring on this cell — uses HexBoard.hintCellIdx.
  cellHighlight?: number;
  /// Draw HintArrow from the bag tile to a target cell.
  arrow?: ArrowRef;
  /// Step advancement: either a tap-Next button or a predicate that
  /// fires from gameplay. Predicate steps may also have a timeout.
  advance:
    | { kind: 'next' }
    | { kind: 'predicate'; check: (state: GameState, evals: EvaluatedConstraint[]) => boolean; timeoutMs?: number };
  /// Last step of the LAST stage shows "Done" instead of "Next".
  isFinal?: boolean;
}

export interface TutorialStage {
  id: string;
  title: string;
  puzzle: Puzzle;
  steps: TutorialStep[];
}

// ──────────────────────────────────────────────────────────────────────
// Stage 1: Welcome + Sum (interactive, size-2)
// ──────────────────────────────────────────────────────────────────────
//
// Re-uses the original size-2 Σ-only puzzle. cells[0..3] empty, [4..6]
// locked 2/3/5; bag = three 1s + a 5. Solvable by 1,1,1,5 in that order.
const STAGE_1_PUZZLE: Puzzle = {
  difficulty: 'Easy',
  name: 'Trine',
  cells: [
    { kind: 'empty' },
    { kind: 'empty' },
    { kind: 'empty' },
    { kind: 'empty' },
    { kind: 'locked', value: 2 },
    { kind: 'locked', value: 3 },
    { kind: 'locked', value: 5 },
  ],
  bag: [
    { id: 't0', value: 1 },
    { id: 't1', value: 1 },
    { id: 't2', value: 1 },
    { id: 't3', value: 5 },
  ],
  constraints: [
    { axis: 'nesw', index: 0, type: 'sum', value: 2 },
    { axis: 'col',  index: 0, type: 'sum', value: 2 },
    { axis: 'nesw', index: 1, type: 'sum', value: 9 },
    { axis: 'nwse', index: 2, type: 'sum', value: 3 },
  ],
};

// Helper: a placement step's predicate fires once at least N tiles
// are on the board. Tracks total placed count rather than the specific
// cell, so a player who places out of order still advances cleanly.
const placedAtLeast = (n: number) => ({
  kind: 'predicate' as const,
  check: (s: GameState) => s.cells.filter(c => c.kind === 'placed').length >= n,
});

const STAGE_1_STEPS: TutorialStep[] = [
  {
    id: 's1-place-1',
    message:
      'Welcome to Hexponents. Drag the highlighted 1 onto the highlighted cell. The Σ chip means sum-equals — these two open cells need to add up to 2.',
    chipHighlight: { axis: 'nesw', index: 0 },
    cellHighlight: 0,
    arrow: { tileId: 't0', toCell: 0 },
    advance: placedAtLeast(1),
  },
  {
    id: 's1-place-2',
    message:
      'Now this column. It also needs to sum to 2 — drop another 1 onto the highlighted cell.',
    chipHighlight: { axis: 'col', index: 0 },
    cellHighlight: 1,
    arrow: { tileId: 't1', toCell: 1 },
    advance: placedAtLeast(2),
  },
  {
    id: 's1-place-3',
    message:
      'Back to the diagonal — it still needs to hit 2. One more 1 on the highlighted cell closes it out.',
    chipHighlight: { axis: 'nesw', index: 0 },
    cellHighlight: 2,
    arrow: { tileId: 't2', toCell: 2 },
    advance: placedAtLeast(3),
  },
  {
    id: 's1-place-4',
    message:
      'Last tile. This line wants to sum to 9. With a 1 and the locked 3 already on it, the 5 finishes it.',
    chipHighlight: { axis: 'nesw', index: 1 },
    cellHighlight: 3,
    arrow: { tileId: 't3', toCell: 3 },
    advance: placedAtLeast(4),
  },
  {
    id: 's1-solved',
    message: 'Every chip green — solved. More rules ahead.',
    advance: { kind: 'next' },
  },
];

// ──────────────────────────────────────────────────────────────────────
// Stage 2: Constraint glossary (passive, all-locked size-3)
// ──────────────────────────────────────────────────────────────────────
//
// 19-cell board, every cell locked, every line that carries a chip
// already passes. The player cannot interact; the stage is a
// walkthrough. Each step pins one chip with an explanation.
//
// Lines used (size-3 line indexing — see HexGrid line layout):
//   col[0]=[0,1,2]     allEven
//   col[1]=[3,4,5,6]   allNeg
//   col[2]=[7..11]     sum 28
//   col[3]=[12..15]    product 24
//   col[4]=[16,17,18]  contains {2,3,5}
//   nesw[0]=[0,3,7]    sumGte 5
//   nesw[2]=[2,5,9,13,16] negCount 1
//   nesw[3]=[6,10,14,17]  allOdd
//   nesw[4]=[11,15,18] sumLte 20
//   nwse[0]=[7,12,16]  noNeg
const STAGE_2_PUZZLE: Puzzle = (() => {
  const values = [
    // col 0 (3): 0,1,2
    4, 2, 6,
    // col 1 (4): 3,4,5,6
    -1, -2, -3, -5,
    // col 2 (5): 7,8,9,10,11
    2, 3, 5, 7, 11,
    // col 3 (4): 12,13,14,15
    1, 2, 3, 4,
    // col 4 (3): 16,17,18
    2, 3, 5,
  ];
  return {
    difficulty: 'Easy',
    name: 'Glossary',
    cells: values.map(v => ({ kind: 'locked' as const, value: v })),
    bag: [],
    constraints: [
      { axis: 'col',  index: 2, type: 'sum',      value: 28 },
      { axis: 'col',  index: 3, type: 'product',  value: 24 },
      { axis: 'nesw', index: 0, type: 'sumGte',   value: 5  },
      { axis: 'nesw', index: 4, type: 'sumLte',   value: 20 },
      { axis: 'col',  index: 0, type: 'allEven' },
      { axis: 'nesw', index: 3, type: 'allOdd' },
      { axis: 'nwse', index: 0, type: 'noNeg' },
      { axis: 'col',  index: 1, type: 'allNeg' },
      { axis: 'nesw', index: 2, type: 'negCount', value: 1 },
      { axis: 'col',  index: 4, type: 'contains', value: [2, 3, 5] },
    ],
  };
})();

const STAGE_2_STEPS: TutorialStep[] = [
  {
    id: 's2-intro',
    message: "Every chip is a rule on the line it points to. Here are all the kinds you'll see.",
    advance: { kind: 'next' },
  },
  {
    id: 's2-sum',
    message: "Σ N — the line's tile values must add to N.",
    chipHighlight: { axis: 'col', index: 2 },
    advance: { kind: 'next' },
  },
  {
    id: 's2-product',
    message: "× N — the line's tile values must multiply to N.",
    chipHighlight: { axis: 'col', index: 3 },
    advance: { kind: 'next' },
  },
  {
    id: 's2-sumGte',
    message: "≥ N — the line's sum must be at least N.",
    chipHighlight: { axis: 'nesw', index: 0 },
    advance: { kind: 'next' },
  },
  {
    id: 's2-sumLte',
    message: "≤ N — the line's sum must be at most N.",
    chipHighlight: { axis: 'nesw', index: 4 },
    advance: { kind: 'next' },
  },
  {
    id: 's2-even',
    message: 'Even — every tile on this line must be even.',
    chipHighlight: { axis: 'col', index: 0 },
    advance: { kind: 'next' },
  },
  {
    id: 's2-odd',
    message: 'Odd — every tile on this line must be odd.',
    chipHighlight: { axis: 'nesw', index: 3 },
    advance: { kind: 'next' },
  },
  {
    id: 's2-noNeg',
    message: 'Positive — every tile on this line must be zero or positive (no negatives).',
    chipHighlight: { axis: 'nwse', index: 0 },
    advance: { kind: 'next' },
  },
  {
    id: 's2-allNeg',
    message: 'All Negative — every tile on this line must be negative.',
    chipHighlight: { axis: 'col', index: 1 },
    advance: { kind: 'next' },
  },
  {
    id: 's2-negCount',
    message: 'N Negatives — the line must have exactly that many negative tiles.',
    chipHighlight: { axis: 'nesw', index: 2 },
    advance: { kind: 'next' },
  },
  {
    id: 's2-contains',
    message: '{a, b, c} — this exact set of values must all appear somewhere on the line.',
    chipHighlight: { axis: 'col', index: 4 },
    advance: { kind: 'next' },
  },
];

// ──────────────────────────────────────────────────────────────────────
// Stage 3: Restrictions (interactive) — size-2
// ──────────────────────────────────────────────────────────────────────
//
// Two empty restriction cells (Even at idx 2, Prime at idx 3) plus
// five locked cells. Bag: t0=4 (the only even unplaced) and t1=5 (the
// only prime). Constraints solve when both tiles land in their homes.
const STAGE_3_PUZZLE: Puzzle = {
  difficulty: 'Easy',
  name: 'Restrictions',
  cells: [
    { kind: 'locked', value: 2 },                  // 0
    { kind: 'locked', value: 4 },                  // 1
    { kind: 'empty', _restriction: 'even' },       // 2
    { kind: 'empty', _restriction: 'prime' },      // 3
    { kind: 'locked', value: 6 },                  // 4
    { kind: 'locked', value: 1 },                  // 5
    { kind: 'locked', value: 3 },                  // 6
  ],
  bag: [
    { id: 't0', value: 4 },
    { id: 't1', value: 5 },
  ],
  constraints: [
    // col[1]=[2,3,4] sum = 4+5+6 = 15
    { axis: 'col',  index: 1, type: 'sum', value: 15 },
    // nwse[0]=[2,5] sum = 4+1 = 5
    { axis: 'nwse', index: 0, type: 'sum', value: 5 },
    // nesw[1]=[1,3,5] sum = 4+5+1 = 10
    { axis: 'nesw', index: 1, type: 'sum', value: 10 },
  ],
};

const STAGE_3_STEPS: TutorialStep[] = [
  // 2026-05-13: dropped the standalone "s3-intro" step. Stage 3 is the
  // first interactive stage after the glossary, so passive intros with
  // no visual cue let the player wander; the intro copy is now merged
  // into the first actionable step.
  {
    id: 's3-even',
    message: 'Some empty cells only accept certain tile values — their label tells you which. Even cells take only even tiles. Drag the 4 onto the highlighted cell.',
    cellHighlight: 2,
    arrow: { tileId: 't0', toCell: 2 },
    advance: {
      kind: 'predicate',
      check: (s) => s.cells[2]?.kind === 'placed',
    },
  },
  {
    id: 's3-prime',
    message: 'Prime cells accept only primes (2, 3, 5, 7…). Drop the 5 here.',
    cellHighlight: 3,
    arrow: { tileId: 't1', toCell: 3 },
    advance: {
      kind: 'predicate',
      check: (s) => s.cells[3]?.kind === 'placed',
    },
  },
  {
    id: 's3-others',
    message: "Other restrictions you'll meet: Odd, +, −, |x|≥5 (magnitude high), |x|≤4 (magnitude low).",
    advance: { kind: 'next' },
  },
];

// ──────────────────────────────────────────────────────────────────────
// Stage 4: Modifiers (interactive) — size-2
// ──────────────────────────────────────────────────────────────────────
//
// Two empty modifier cells (×2 at idx 2, +5 at idx 3). Locked cells and
// constraints are designed so the only solution is t0=3 → idx2 (effective
// 6) and t1=2 → idx3 (effective 7).
const STAGE_4_PUZZLE: Puzzle = {
  difficulty: 'Easy',
  name: 'Modifiers',
  cells: [
    { kind: 'locked', value: 2 },                  // 0
    { kind: 'locked', value: 4 },                  // 1
    { kind: 'empty', modifier: '×2' },             // 2
    { kind: 'empty', modifier: '+5' },             // 3
    { kind: 'locked', value: 3 },                  // 4
    { kind: 'locked', value: 1 },                  // 5
    { kind: 'locked', value: 5 },                  // 6
  ],
  bag: [
    { id: 't0', value: 3 },
    { id: 't1', value: 2 },
  ],
  constraints: [
    // col[1]=[2,3,4] effective: (3*2) + (2+5) + 3 = 6 + 7 + 3 = 16
    { axis: 'col',  index: 1, type: 'sum', value: 16 },
    // nwse[0]=[2,5] effective: 6 + 1 = 7
    { axis: 'nwse', index: 0, type: 'sum', value: 7  },
    // nesw[1]=[1,3,5] effective: 4 + 7 + 1 = 12
    { axis: 'nesw', index: 1, type: 'sum', value: 12 },
  ],
};

const STAGE_4_STEPS: TutorialStep[] = [
  // 2026-05-13: dropped the standalone "s4-intro" — same reason as s3
  // above. Stage 4 is interactive; the passive intro let players move
  // tiles before any visual cue showed them where.
  {
    id: 's4-times',
    message: "Modifier cells transform the tile you place for the line's rule. *2 — drop a tile here and the line treats it as double. Place the 3 on the highlighted cell.",
    cellHighlight: 2,
    arrow: { tileId: 't0', toCell: 2 },
    advance: {
      kind: 'predicate',
      check: (s) => s.cells[2]?.kind === 'placed',
    },
  },
  {
    id: 's4-plus',
    message: '+5 — the tile counts as itself plus five. Drop the 2 here.',
    cellHighlight: 3,
    arrow: { tileId: 't1', toCell: 3 },
    advance: {
      kind: 'predicate',
      check: (s) => s.cells[3]?.kind === 'placed',
    },
  },
  {
    id: 's4-others',
    message: "Modifiers you'll see: *N, +N, −N, ÷N, ² (square), ³ (cube).",
    advance: { kind: 'next' },
  },
];

// ──────────────────────────────────────────────────────────────────────
// Stage 5: Gaps + wrap-up (passive) — size-2
// ──────────────────────────────────────────────────────────────────────
//
// Six locked tiles + one gap. No interaction; we just call out the
// dashed-outline gap cell, then end the tutorial.
const STAGE_5_PUZZLE: Puzzle = {
  difficulty: 'Easy',
  name: 'Gaps',
  cells: [
    { kind: 'gap' },                // 0
    { kind: 'locked', value: 1 },   // 1
    { kind: 'locked', value: 2 },   // 2
    { kind: 'locked', value: 3 },   // 3
    { kind: 'locked', value: 4 },   // 4
    { kind: 'locked', value: 5 },   // 5
    { kind: 'locked', value: 6 },   // 6
  ],
  bag: [],
  constraints: [],
};

const STAGE_5_STEPS: TutorialStep[] = [
  {
    id: 's5-gap',
    message: 'Gap cells are holes — no tile goes here. They show up on Hard and Expert puzzles to break up the lines.',
    cellHighlight: 0,
    advance: { kind: 'next' },
  },
  {
    id: 's5-done',
    message: "Higher difficulties combine all of these. A few more things worth knowing →",
    advance: { kind: 'next' },
    // isFinal moved to the last step of the new "What's new" stage.
  },
];

// ──────────────────────────────────────────────────────────────────────
// Stage 6 — "What's new" — informational recap of features added after
// the original 5-stage tutorial: cell-focus highlighting, the redesigned
// hint button (auto-places), custom puzzles via Browse + Design, and
// badges. Reuses STAGE_1_PUZZLE so the player has a familiar layout to
// look at; steps are text-only with cellHighlight + chipHighlight to
// gesture at where things live.
// ──────────────────────────────────────────────────────────────────────
const STAGE_6_PUZZLE: Puzzle = STAGE_1_PUZZLE;

const STAGE_6_STEPS: TutorialStep[] = [
  {
    id: 's6-focus',
    message: "Tip: click any empty cell to see which constraints touch it — the chips on its lines light up and the matching bag tiles get highlighted too.",
    cellHighlight: 4,
    advance: { kind: 'next' },
  },
  {
    id: 's6-hint',
    message: "Stuck? The Hint button now solves one cell for you. Click any cell first to see what fits — that part's free.",
    advance: { kind: 'next' },
  },
  {
    id: 's6-customs',
    message: "Browse community-made puzzles from the Browse button up top. Premium subscribers can design and share their own from the Design button.",
    advance: { kind: 'next' },
  },
  {
    id: 's6-badges',
    message: "You earn badges as you play — solve streaks, total counts, custom maps you make or like. Click your name in the header to see your profile + which ones are unlocked.",
    advance: { kind: 'next' },
    isFinal: true,
  },
];

// ──────────────────────────────────────────────────────────────────────
// Stage list (export)
// ──────────────────────────────────────────────────────────────────────
export const TUTORIAL_STAGES: TutorialStage[] = [
  { id: 'welcome',      title: 'Welcome',      puzzle: STAGE_1_PUZZLE, steps: STAGE_1_STEPS },
  { id: 'glossary',     title: 'Glossary',     puzzle: STAGE_2_PUZZLE, steps: STAGE_2_STEPS },
  { id: 'restrictions', title: 'Restrictions', puzzle: STAGE_3_PUZZLE, steps: STAGE_3_STEPS },
  { id: 'modifiers',    title: 'Modifiers',    puzzle: STAGE_4_PUZZLE, steps: STAGE_4_STEPS },
  { id: 'gaps',         title: 'Gaps',         puzzle: STAGE_5_PUZZLE, steps: STAGE_5_STEPS },
  { id: 'whats-new',    title: "What's new",   puzzle: STAGE_6_PUZZLE, steps: STAGE_6_STEPS },
];

// Total step count across stages — used by the step indicator.
export const TUTORIAL_TOTAL_STEPS: number = TUTORIAL_STAGES.reduce((n, s) => n + s.steps.length, 0);

// Legacy single-stage exports used by older callers — kept temporarily so
// imports don't break if any code outside Tutorial.tsx referenced them.
export const TUTORIAL_PUZZLE: Puzzle = STAGE_1_PUZZLE;
export const TUTORIAL_STEPS: TutorialStep[] = STAGE_1_STEPS;
